import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FileSpreadsheet, RefreshCw, Settings2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { errMsg } from '@/services/staffAttendanceApi';
import { tallyApi } from '@/services/ewayBillApi';
import { fmtDT } from '@/components/ewayBill/shared';

const Warn = ({ children, testId }) => <span className="text-sm font-medium text-rose-600" data-testid={testId}>{children}</span>;

/** Live state of the Tally link (script on the Tally PC) + what the accountant has to do in Tally. */
export const TallyLinkCard = ({ onSetup, onGstin, tick, onCounts }) => {
  const [s, setS] = useState(null);
  const cb = useRef(onCounts);
  cb.current = onCounts;
  const load = useCallback(() => tallyApi.status().then((x) => { setS(x); cb.current?.(x.counts); }).catch(() => {}), []);
  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [load, tick]);
  if (!s) return null;
  const sync = () => tallyApi.syncMasters().then(() => { toast.success('The Tally link will re-read ledgers & items in a few seconds'); load(); })
    .catch((e) => toast.error(errMsg(e, 'Failed')));
  const label = s.online ? 'Tally link online' : s.has_key ? 'Tally link offline' : 'Tally link not set up';
  return (
    <div className="rounded-xl bg-white shadow-sm p-4 space-y-3" data-testid="tally-link-card">
      <div className="flex flex-wrap items-center gap-3">
        <span className={`h-2.5 w-2.5 rounded-full ${s.online ? 'bg-emerald-500 animate-pulse' : 'bg-rose-500'}`} />
        <b data-testid="tally-link-status">{label}</b>
        <span className="text-sm text-gray-500 max-w-[320px] truncate" title={s.agent_host || ''} data-testid="tally-link-seen">
          {s.last_seen ? `last seen ${fmtDT(s.last_seen)}` : ''}{s.agent_host ? ` · ${s.agent_host}` : ''}</span>
        {s.online && s.tally_ok === false && <Warn testId="tally-unreachable">TallyPrime not reachable: {s.tally_error}</Warn>}
        {s.online && s.tally_ok !== false && s.company_open === false && <Warn testId="tally-company-closed">Company “{s.company}” isn't open in TallyPrime</Warn>}
        <div className="ml-auto flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={onGstin} data-testid="tally-gstin-btn"><FileSpreadsheet className="w-4 h-4 mr-1" />GSTIN Excel for Tally</Button>
          <Button size="sm" variant="outline" onClick={sync} disabled={!s.has_key} data-testid="tally-sync-btn"><RefreshCw className="w-4 h-4 mr-1" />Re-read Tally masters</Button>
          {s.can_settings && <Button size="sm" onClick={onSetup} className="bg-[#1A1F2E] hover:bg-black" data-testid="tally-setup-btn"><Settings2 className="w-4 h-4 mr-1" />Tally link setup</Button>}
        </div>
      </div>
      <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-gray-600">
        <span>Company <b data-testid="tally-company">{s.company || '— not set —'}</b></span>
        <span>Tally masters <b data-testid="tally-masters">{s.masters.ledgers} ledgers · {s.masters.items} items</b>{s.masters.synced_at ? ` (read ${fmtDT(s.masters.synced_at)})` : ''}</span>
        {s.counts.QUEUED > 0 && <span data-testid="tally-queued-count"><b>{s.counts.QUEUED}</b> waiting for the link</span>}
        {s.counts.TALLY_ERROR > 0 && <Warn testId="tally-error-count">{s.counts.TALLY_ERROR} with Tally errors</Warn>}
      </div>
      {s.counts.IN_TALLY > 0 && (
        <p className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-sky-900" data-testid="tally-send-hint">
          <b>{s.counts.IN_TALLY} waiting in Tally.</b> In TallyPrime press <b>Alt+G → e-Way Bill</b>, then <b>Exchange → Send for e-Way Bill</b>.
          WMS picks up the numbers within a minute.
        </p>
      )}
    </div>
  );
};

export default TallyLinkCard;
